import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { Check, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { CirclePlus } from 'lucide-react';
import { sortOptionsAlphabetically } from '@/lib/utils';
interface MultiSelectOption {
  value: string;
  label1: string;
  label2?: string;
}

export interface MultiSelectProps {
  name: string;
  label: string;
  options: MultiSelectOption[] | null;
  mode?: 'edit' | 'create';
  addNewOption?: {
    label: string;
    onClick: () => void;
  };
  placeholder?: string;
  labelToDisplay: {
    dropdown: 'label1' | 'label2';
    selectedOption: 'label1' | 'label2';
  };
  disabledOptions?: string[];
  disabled?: boolean;
  /** Show options A–Z by their dropdown label. Only for lists built in the web app; API lists already arrive in order. */
  sortOptions?: boolean;
}

const FormFieldMultiSelect: React.FC<MultiSelectProps> = ({
  name,
  label,
  options,
  addNewOption,
  placeholder = 'Select options',
  labelToDisplay,
  disabledOptions,
  disabled,
  sortOptions = false,
}) => {
  const { control } = useFormContext();
  const { field } = useController({ name, control });
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isOptionClicked = useRef(false);

  useEffect(() => {
    if (field.value && Array.isArray(field.value)) {
      setSelectedValues(field.value);
    }
  }, [field.value]);

  const toggleOption = (value: string) => {
    setSelectedValues((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const displayOptions = useMemo(() => {
    if (!options) return [];
    return sortOptions
      ? sortOptionsAlphabetically(options, (option) => option[labelToDisplay.dropdown] ?? option.label1)
      : options;
  }, [options, sortOptions, labelToDisplay.dropdown]);

  const filteredOptions = displayOptions.filter((option) =>
    option.label1.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    field.onBlur();
  }, [isDropdownOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && focusedOptionIndex === null) {
      event.preventDefault();
      setDropdownOpen((prev) => !prev);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedOptionIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, filteredOptions.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedOptionIndex((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
    } else if (event.key === 'Enter' && focusedOptionIndex !== null) {
      event.preventDefault();
      const option = filteredOptions[focusedOptionIndex];
      if (option) {
        toggleOption(option.value);
      }
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    setFocusedOptionIndex(null);
    if (!isOptionClicked.current && !dropdownRef.current?.contains(event.relatedTarget as Node)) {
      setDropdownOpen(false);
    }
    isOptionClicked.current = false;
    field.onBlur();
  };

  return (
    <div className="flex flex-col w-full gap-1" ref={dropdownRef}>
      <label htmlFor={name} className="block text-sm font-[500]">
        {label}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <div className="relative mt-2">
            <div
              className={`border-[1px] 
                h-12 rounded-md border-bordercolor
                p-2 cursor-pointer flex items-center justify-between
               focus:outline-none focus:border-focuscolor ${disabled ? 'cursor-not-allowed pointer-events-none opacity-50' : ''} ${isDropdownOpen && 'border-focuscolor'}`}
              onClick={() => setDropdownOpen((prev) => !prev)}
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
            >
              <div
                className=" gap-2 max-w-full w-full whitespace-nowrap overflow-x-scroll overflow-y-hidden block scrollbar-hide"
                role="region"
                aria-label="Selected email addresses"
              >
                {!isDropdownOpen && selectedValues.length === 0 ? (
                  <span className="px-[10px] py-[14px] text-[#676767] text-sm">{placeholder}</span>
                ) : (
                  ''
                )}
                {isDropdownOpen && !disabled ? (
                  <Input
                    type="text"
                    disabled={disabled}
                    placeholder={placeholder}
                    className="border-none outline-0 focus:border-none h-6 placeholder:text-[#676767]"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    ref={searchInputRef}
                  />
                ) : (
                  selectedValues.length > 0 && (
                    <span className="px-2 py-1 text-sm ">
                      {selectedValues
                        .map((val) => options?.find((option) => option.value === val)?.[labelToDisplay.selectedOption])
                        .filter((label) => label !== undefined)
                        .join(', ')}
                    </span>
                  )
                )}
              </div>
              <div className="flex flex-row items-center">
                <ChevronDown className="w-4 h-4 text-gray-500 cursor-pointer" />
              </div>
            </div>
            {isDropdownOpen && (
              <ul className="absolute z-10 w-full mt-1 max-h-60 text-[14px] bg-white border border-gray-300 rounded-md shadow-lg break-all overflow-auto scrollbar-hide">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <li
                      key={option.value}
                      className={`p-2 min-h-[32px] flex ${disabledOptions?.includes(option.value) ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''} ${
                        selectedValues.includes(option.value) ? 'bg-[#EDF5FF]' : ''
                      } items-center justify-start gap-6 hover:bg-gray-100 cursor-pointer ${
                        focusedOptionIndex === index ? 'bg-gray-200' : ''
                      }`}
                      onMouseDown={() => {
                        isOptionClicked.current = true;
                      }}
                      onClick={() => {
                        if (disabledOptions?.includes(option.value)) return;
                        toggleOption(option.value);
                        field.onChange(
                          selectedValues.includes(option.value)
                            ? selectedValues.filter((item) => item !== option.value)
                            : [...selectedValues, option.value]
                        );
                      }}
                    >
                      <div className="w-3 px-2">
                        {selectedValues.includes(option.value) && (
                          <Check color="#0A2240" className="w-4 h-4" strokeWidth="2.5" />
                        )}
                      </div>
                      <span
                        className={`${
                          selectedValues.includes(option.value)
                            ? 'text-primary font-[500] bg-[#EDF5FF]'
                            : 'text-[#676767]'
                        }`}
                      >
                        {option?.[labelToDisplay.dropdown]}
                      </span>
                    </li>
                  ))
                ) : addNewOption ? null : (
                  <li className="p-5 h-[32px] flex items-center justify-center text-gray-500">No Results</li>
                )}
                {addNewOption && (
                  <li
                    className="p-2 h-[32px] flex items-center justify-start text-gray-500 new-email cursor-pointer"
                    onMouseDown={() => {
                      isOptionClicked.current = true;
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      addNewOption?.onClick();
                    }}
                  >
                    <div className="pl-6 align-middle items-center flex gap-4 font-400">
                      <CirclePlus className="w-5 h-5" color="#1B8652" />
                      <span className="text-primary">{addNewOption?.label}</span>
                    </div>
                  </li>
                )}
              </ul>
            )}
            {error && <p className="text-error text-xs py-3">{error.message}</p>}
          </div>
        )}
      />
    </div>
  );
};

export default FormFieldMultiSelect;
